import React, { FC, Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { getClientSearchTemplate } from 'apollo/templates/client-directory';
import { ClientDirectoryFilter, ClientType, CustomerType, LabClient } from 'models/client-directory';
import { debounce } from 'lodash';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import ClientDirectoryList from 'components/client-directory/client-directory-list/client-directory-list.component';
import ClientDirectoryPageActions from 'components/client-directory/client-directory-page-actions/client-directory-page-actions.component';
import { downloadClientListPdf } from 'components/order-details/main-order-view/document-print-actions/files';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { LoadingButton } from '@mui/lab';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useLabClients } from 'hooks/useLabClients';

const checkIsFiltered = (filters: ClientDirectoryFilter) => {
    return (
        !!filters.freeText ||
        filters.type !== CustomerType.ALL ||
        !filters.active ||
        filters.transportProvider !== 'ALL' ||
        !!filters.country ||
        filters.materialPriceList !== 'ALL_MATERIAL_PRICE_LISTS' ||
        filters.actionPriceList !== 'ALL_ACTION_PRICE_LISTS'
    );
};

const ClientDirectory: FC = () => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const [labClients, setLabClients] = useState<LabClient[]>([] as LabClient[]);
    const filters = JSON.parse(
        localStorage.getItem('clientListFilter') ||
            JSON.stringify({
                freeText: '',
                type: CustomerType.ALL,
                active: true,
                transportProvider: 'ALL',
                country: '',
                materialPriceList: 'ALL_MATERIAL_PRICE_LISTS',
                actionPriceList: 'ALL_ACTION_PRICE_LISTS',
            }),
    );
    const [filter, setFilter] = useState<ClientDirectoryFilter>(filters);
    const [spinner, setSpinner] = useState(false);
    const [isFiltered, setIsFiltered] = useState(checkIsFiltered(filters));
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { getLabClients, fetchMoreLabClients, isLabClientListLoading } = useLabClients(
        setLabClients,
        ClientType.CUSTOMER,
    );

    const [getSearchClients, { loading: clientSearchLoading }] = useLazyQuery(getClientSearchTemplate, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        onCompleted({ clientSearch }) {
            setLabClients(clientSearch);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        setIsFiltered(checkIsFiltered(filter));
        const isInitialFilter =
            filter.freeText === '' &&
            filter.type === CustomerType.ALL &&
            filter.active === true &&
            filter.transportProvider === 'ALL' &&
            filter.country === '' &&
            filter.materialPriceList === 'ALL_MATERIAL_PRICE_LISTS' &&
            filter.actionPriceList === 'ALL_ACTION_PRICE_LISTS';

        if (isInitialFilter) {
            getLabClients({ variables: { offset: 0, limit: 20, groupedByParent: true } });
        } else {
            getSearchClients({ variables: { filters: filter, groupedByParent: true } });
        }
    }, [filter]);

    const handleFetchMore = () => {
        fetchMoreLabClients();
    };

    const handleFilterChange = useCallback(
        debounce(
            ({
                freeText,
                type,
                active,
                transportProvider,
                country,
                materialPriceList,
                actionPriceList,
            }: ClientDirectoryFilter) => {
                const listFilters = {
                    freeText: freeText,
                    type: type,
                    active: active,
                    transportProvider: transportProvider,
                    country: country,
                    materialPriceList: materialPriceList,
                    actionPriceList: actionPriceList,
                };
                setIsFiltered(checkIsFiltered(listFilters));
                setFilter(listFilters);
                localStorage.setItem('clientListFilter', JSON.stringify(listFilters));
            },
            700,
        ),
        [],
    );

    const handleActionPriceListPdf = async () => {
        try {
            setSpinner(true);
            await downloadClientListPdf('client-list.pdf');
        } catch (error) {
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: (error as Error).message,
            });
        } finally {
            setSpinner(false);
        }
    };

    return (
        <Fragment>
            <Box display="flex">
                <Grid container spacing={1}>
                    <Grid item sm={7} xs={12}>
                        <Typography variant="h1" component="h1">
                            {translate('client-directory:CLIENT_DIRECTORY')}
                        </Typography>
                    </Grid>
                    <Grid item sm={5} xs={12} display="flex" alignItems="center" justifyContent="flex-end">
                        <LoadingButton
                            loading={spinner}
                            onClick={handleActionPriceListPdf}
                            startIcon={<PictureAsPdfIcon />}
                        >
                            {translate('common:EXPORT_PDF')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </Box>
            <ClientDirectoryPageActions clientListFilter={filter} onFilterChange={handleFilterChange} />
            {isLabClientListLoading || clientSearchLoading ? <LinearProgress color="secondary" /> : undefined}
            <ClientDirectoryList
                filteredClients={labClients}
                onFetchMoreChange={handleFetchMore}
                isFiltered={isFiltered}
            />
        </Fragment>
    );
};

export default ClientDirectory;
